/**
 * Backend Configuration Types
 * All configuration comes from backend API on app startup
 */

export interface ThemeConfig {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  backgroundColor: string;
  textColor: string;
  borderColor: string;
  successColor: string;
  warningColor: string;
  errorColor: string;
  fontFamily: string;
  logoUrl?: string;
  faviconUrl?: string;
}

export interface TabConfig {
  id: string;
  label: string;
  icon?: string;
  route: string;
  enabled: boolean;
  order: number;
  requiredRoles?: string[];
}

export interface FeatureConfig {
  id: string;
  enabled: boolean;
  label: string;
  description?: string;
}

export interface BackendConfig {
  // Branding
  appName: string;
  appVersion: string;
  companyName: string;
  companyLogo: string;
  supportEmail: string;
  supportPhone?: string;
  companyWebsite?: string;

  // Theme
  theme: ThemeConfig;

  // Navigation & Tabs
  navigationTabs: TabConfig[];

  // Features (what user can see/access)
  features: {
    dashboard: FeatureConfig;
    cddRecords: FeatureConfig;
    profiles: FeatureConfig;
    invitations: FeatureConfig;
    billing: FeatureConfig;
    account: FeatureConfig;
    settings: FeatureConfig;
    [key: string]: FeatureConfig;
  };

  // UI Configuration
  ui: {
    showHeader: boolean;
    showFooter: boolean;
    showSidebar: boolean;
    sidebarPosition: 'left' | 'right';
    headerPosition: 'top' | 'bottom';
    enableDarkMode: boolean;
    enableNotifications: boolean;
  };

  // Auth Configuration
  auth: {
    idleTimeoutSeconds: number;
    warningTimeoutSeconds: number;
    passwordMinLength: number;
    requireSpecialChar: boolean;
    requireNumbers: boolean;
    requireUppercase: boolean;
    requireLowercase: boolean;
  };

  // Account types available for this organization
  accountTypes: {
    id: string;
    name: string;
    description: string;
    icon: string;
    features: string[];
    enabled: boolean;
  }[];

  // Custom messages
  messages: {
    welcomeMessage: string;
    loginHeading: string;
    loginSubtext: string;
    /**
     * AL-470: sets the expectation on the login form itself, so a first-time
     * user is not surprised by the set-password step. Kept separate from
     * loginSubtext, which answers a different question (where to find the
     * username) and should not have to be rewritten to carry this.
     */
    loginTempPasswordHint: string;
    dashboardWelcome: string;
    [key: string]: string;
  };

  // API Configuration
  apiBaseUrl: string;
  apiTimeout: number;

  // Mode Configuration
  mode?: 'real';

  // Metadata
  configVersion: string;
  lastUpdated: string;
}

// Default fallback config (safe defaults if backend is down)
export const DEFAULT_BACKEND_CONFIG: BackendConfig = {
  appName: 'CDD Management',
  appVersion: '1.0.0',
  companyName: 'Organization',
  // Bundled asset: portal chrome must not depend on a third-party placeholder.
  companyLogo: '/arcaml_logo.svg',
  supportEmail: 'support@example.com',
  companyWebsite: 'https://example.com',

  theme: {
    primaryColor: '#1967d2',
    secondaryColor: '#0f3a7d',
    accentColor: '#1967d2',
    backgroundColor: '#ffffff',
    textColor: '#333333',
    borderColor: '#e0e0e0',
    successColor: '#10b981',
    warningColor: '#f59e0b',
    errorColor: '#ef4444',
    fontFamily: 'Inter, system-ui, sans-serif',
  },

  navigationTabs: [
    {
      id: 'dashboard',
      label: 'Dashboard',
      route: '/portal/dashboard',
      enabled: true,
      order: 1,
    },
    {
      id: 'riskAssessment',
      label: 'Risk Assessment',
      route: '/portal/risk-assessment',
      enabled: true,
      order: 2,
    },
    {
      id: 'invitations',
      label: 'Invitations',
      route: '/portal/invitations',
      enabled: true,
      order: 3,
    },
    {
      id: 'cddRecords',
      label: 'CDD Records',
      route: '/portal/records',
      enabled: true,
      order: 4,
    },
    {
      id: 'forms',
      label: 'Forms',
      route: '/portal/forms',
      enabled: true,
      order: 5,
    },
    {
      id: 'selfAssessment',
      label: 'Self Assessment Documents',
      route: '/portal/self-assessment',
      enabled: true,
      order: 6,
    },
    {
      id: 'account',
      label: 'Account',
      route: '/portal/account',
      enabled: true,
      order: 7,
    },
    {
      id: 'faqs',
      label: 'FAQ',
      route: '/portal/faqs',
      enabled: true,
      order: 8,
    },
  ], features: {
    dashboard: { id: 'dashboard', enabled: true, label: 'Dashboard' },
    cddRecords: { id: 'cddRecords', enabled: true, label: 'CDD Records' },
    profiles: { id: 'profiles', enabled: true, label: 'Profiles' },
    invitations: { id: 'invitations', enabled: true, label: 'Invitations' },
    billing: { id: 'billing', enabled: true, label: 'Billing' },
    account: { id: 'account', enabled: true, label: 'Account' },
    faqs: { id: 'faqs', enabled: true, label: 'Frequently Asked Questions' },
    forms: { id: 'forms', enabled: true, label: 'Forms' },
    riskAssessment: { id: 'riskAssessment', enabled: true, label: 'Risk Assessment' },
    selfAssessment: { id: 'selfAssessment', enabled: true, label: 'Self Assessment Documents' },
    settings: { id: 'settings', enabled: false, label: 'Settings' },
  },

  ui: {
    showHeader: true,
    showFooter: false,
    showSidebar: true,
    sidebarPosition: 'left',
    headerPosition: 'top',
    enableDarkMode: false,
    enableNotifications: true,
  },

  auth: {
    idleTimeoutSeconds: 30,
    warningTimeoutSeconds: 5,
    // AL-470: these mirror the live C1 Cognito pool password policy
    // (arcaml-live-c1, ap-southeast-2_KoyBewcxx). Keep them in step with the
    // pool — showing a rule Cognito does not enforce rejects passwords it
    // would accept, which is what drove the password-rejection reports.
    passwordMinLength: 8,
    requireSpecialChar: false, // pool has RequireSymbols: false
    requireNumbers: true,
    requireUppercase: true,
    requireLowercase: true,
  },

  accountTypes: [
    {
      id: 'accounting',
      name: 'Accounting Firm',
      description: 'For accounting and audit firms',
      icon: '📊',
      features: ['cdd-processing', 'reporting', 'team-collaboration'],
      enabled: true,
    },
    {
      id: 'realEstate',
      name: 'Real Estate Agent',
      description: 'For real estate professionals',
      icon: '🏠',
      features: ['cdd-processing', 'client-management'],
      enabled: true,
    },
    {
      id: 'legal',
      name: 'Legal Firm',
      description: 'For legal professionals',
      icon: '⚖️',
      features: ['cdd-processing', 'compliance-tracking'],
      enabled: true,
    },
    {
      id: 'finance',
      name: 'Financial Advisor',
      description: 'For financial advisors',
      icon: '💰',
      features: ['cdd-processing', 'portfolio-management'],
      enabled: true,
    },
  ],

  messages: {
    welcomeMessage: 'Welcome to CDD Management',
    loginHeading: 'Customer Due Diligence Management',
    loginSubtext: 'Use the Username from your welcome email to sign in',
    // AL-470: follows ops-enroll-mfa.html, which sets the same expectation on
    // the OPS login form. Naming the step before it happens is what stops it
    // reading as a failure when it arrives.
    loginTempPasswordHint:
      'If you were sent a temporary password, use it here — you will be asked to choose a new one.',
    dashboardWelcome: 'Welcome to your dashboard',
  },

  apiBaseUrl: 'https://api.example.com',
  apiTimeout: 30000,

  configVersion: '1.0.0',
  lastUpdated: new Date().toISOString(),
};
